// What a raw-material Inquiry line needs cut, per requisition line: `pieces`
// of `pieceMm` each (part length plus cutting allowance), or `looseMm` when
// the line gives only a total (EE-253).
export type StockCutType = {
  pieceMm: number | null;
  pieces: number;
  looseMm: number | null;
};

/** The fewest stock lengths of `lengthMm` that hold every cut: pieces
 * longest first, each into the first length with room; loose millimetres
 * fill what is left, then whole lengths. null when a piece is longer than
 * the length. Mirrors the backend's domain/stock.min_lengths, which still
 * checks the count on save. */
export const minLengths = (
  cuts: StockCutType[],
  lengthMm: number
): number | null => {
  if (!(lengthMm > 0)) return null;
  const pieces = cuts
    .flatMap((cut) =>
      cut.pieceMm != null
        ? Array(Math.max(cut.pieces, 0)).fill(cut.pieceMm)
        : []
    )
    .sort((a, b) => b - a);
  if (pieces.length && pieces[0] > lengthMm) return null;
  const rooms: number[] = [];
  for (const piece of pieces) {
    const i = rooms.findIndex((room) => room >= piece);
    if (i >= 0) rooms[i] -= piece;
    else rooms.push(lengthMm - piece);
  }
  const loose = cuts.reduce((sum, cut) => sum + (cut.looseMm ?? 0), 0);
  const over = loose - rooms.reduce((sum, room) => sum + room, 0);
  // Rounded to 0.001 mm so float dust does not buy a whole extra length.
  const extra =
    over > 0.0005 ? Math.ceil(Math.round((over / lengthMm) * 1e6) / 1e6) : 0;
  return Math.max(rooms.length + extra, 1);
};

/** The longest piece, for saying which one does not fit. */
export const longestPiece = (cuts: StockCutType[]): number | null =>
  cuts.reduce<number | null>(
    (max, cut) =>
      cut.pieceMm != null &&
      cut.pieces > 0 &&
      (max == null || cut.pieceMm > max)
        ? cut.pieceMm
        : max,
    null
  );
